import { useMemo } from 'react';
import {
  LabelOrigin,
  LabelSuggestionDetails,
  LabelType,
  LabelValue,
  LabelValueSource,
  SuggestionLabelValue,
  TimeSignal
} from 'graphql/types';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { useFormattedDuration } from 'modules/notes/common';
import { useMeContext } from 'modules/me';
import { formatTimeFromIsoString } from 'utils/date-time';
import {
  useIsSmartSuggestionSettingEnabled,
  determineLabelAcceptedBy
} from 'modules/timesheet-labels/hooks';
import { useFeatureFlag } from 'modules/common/hooks';

export const useLabelInfo = ({
  note
}: {
  note: TimeSignal;
}): {
  timesheetLabels: TimesheetLabel[];
  customLabels: TimesheetLabel[];
  formattedDuration: string;
  formattedTime: {
    start: string;
    end: string;
  };
} => {
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const me = useMeContext();

  const formattedDuration = useFormattedDuration({
    needsMask: true,
    duration: note.duration ?? 0
  });

  const timesheetLabels = useMemo(
    () =>
      note.labelValues
        ?.filter((l) => {
          if (l.label?.type === LabelType.Comment && !l.value) {
            return false;
          }
          return l.label?.origin !== LabelOrigin.User;
        })
        .map((l) => {
          const retVal: TimesheetLabel = {
            ...l.label,
            value: l.value
          };
          if (isSmartSuggestionEnabled) {
            retVal.valueSource = l.valueSource;

            if (isTimeSignalFederatedSuggestionsEnabled) {
              const suggestion = note?.suggestions?.suggestions?.find(
                (suggestion: LabelSuggestionDetails) =>
                  suggestion?.label?.id === l?.label?.id
              );
              retVal.suggestedValues =
                suggestion?.suggestedValues
                  ?.filter(Boolean)
                  .map(
                    (suggestedValue: SuggestionLabelValue | undefined | null) =>
                      suggestedValue as LabelValue
                  ) ?? [];
            }
          }
          retVal.acceptedBy = determineLabelAcceptedBy(
            retVal.valueSource ?? LabelValueSource.User,
            l.acceptedBy,
            isSmartSuggestionEnabled
          );
          return retVal;
        }),
    [
      note?.suggestions?.suggestions,
      isSmartSuggestionEnabled,
      isTimeSignalFederatedSuggestionsEnabled,
      note.labelValues
    ]
  );

  const customLabels = useMemo(
    () =>
      note.labelValues
        ?.filter((l) => l.label?.origin === LabelOrigin.User && l.value)
        .map((l) => {
          const retVal: TimesheetLabel = {
            ...l.label,
            value: l.value
          };
          if (isSmartSuggestionEnabled) {
            retVal.valueSource = l.valueSource;

            if (isTimeSignalFederatedSuggestionsEnabled) {
              const suggestion = note.suggestions.suggestions.find(
                (suggestion: LabelSuggestionDetails) =>
                  suggestion.label.id === l.label.id
              );
              retVal.suggestedValues =
                suggestion?.suggestedValues
                  ?.filter(Boolean)
                  .map(
                    (suggestedValue: SuggestionLabelValue | undefined | null) =>
                      suggestedValue as LabelValue
                  ) ?? [];
            }
          }
          retVal.acceptedBy = determineLabelAcceptedBy(
            retVal.valueSource ?? LabelValueSource.User,
            l.acceptedBy,
            isSmartSuggestionEnabled
          );

          return retVal;
        }),
    [
      note.suggestions.suggestions,
      isSmartSuggestionEnabled,
      isTimeSignalFederatedSuggestionsEnabled,
      note.labelValues
    ]
  );

  const formattedTime = useMemo(
    () => ({
      start: formatTimeFromIsoString(note.startTime, me),
      end: formatTimeFromIsoString(note.endTime, me)
    }),
    [me, note.endTime, note.startTime]
  );

  return { timesheetLabels, customLabels, formattedDuration, formattedTime };
};
